import axios from 'axios';
import { applyCommonHeaders } from './httpCommon';
import config from '../config';

const ANALYTICS_BASE_URL = (config.VITE_CORE_API_BASE_URL).replace(/\/$/, '');
const EARLYSTAGE_ANALYTICS_PREFIX = '/earlystage/analytics';

export const analyticsApi = axios.create({
  baseURL: ANALYTICS_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

analyticsApi.interceptors.request.use(applyCommonHeaders);

export type AnomalySeverity = 'critical' | 'high' | 'medium' | 'low';
export type AnomalyStatus = 'active' | 'acknowledged' | 'resolved' | 'false_positive';

export interface Anomaly {
  id: number;
  trace_id: string;
  page_id: number;
  tenant_id: number;
  metric: string;
  source: string;
  current_value: number;
  baseline: number;
  delta: number;
  delta_percent: number;
  algorithm: string;
  algorithm_version: string;
  confidence: number;
  severity: AnomalySeverity;
  seasonality_adjusted: boolean;
  window_start: string;
  window_end: string;
  possible_causes: string;
  feature_id: string;
  detected_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  status: AnomalyStatus;
}

export interface AnomalyListResponse {
  anomalies: Anomaly[];
  total: number;
  page: number;
  page_size: number;
}

// Matches actual backend response from /anomalies/stats/summary
export interface AnomalyStatsResponse {
  total_anomalies: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  active_count: number;
  acknowledged_count: number;
  resolved_count: number;
  // Computed property for backward compatibility
  by_severity?: Record<string, number>;
  by_status?: Record<string, number>;
}

export interface ListFilters {
  tenant_id?: number;
  page?: number;
  page_size?: number;
  site_id?: number;
  page_id?: number;
  metric?: string;
  severity?: AnomalySeverity;
  status?: AnomalyStatus;
  days_back?: number;
}

export const fetchAnomalies = (filters: ListFilters = {}) =>
  analyticsApi.get<AnomalyListResponse>(`${EARLYSTAGE_ANALYTICS_PREFIX}/anomalies`, {
    params: { siteId: filters.site_id, significantOnly: false, limit: filters.page_size || 100 },
  });

export const fetchAnomaly = (traceId: string) =>
  analyticsApi.get<Anomaly>(`${EARLYSTAGE_ANALYTICS_PREFIX}/anomalies/${traceId}`);

export const fetchCriticalAnomalies = (filters: Partial<ListFilters> = {}) =>
  analyticsApi.get<AnomalyListResponse>(`${EARLYSTAGE_ANALYTICS_PREFIX}/anomalies`, {
    params: { siteId: filters.site_id, significantOnly: true, limit: filters.page_size || 100 },
  });

export const fetchAnomalyStats = (filters: Partial<ListFilters> = {}) =>
  analyticsApi.get<AnomalyStatsResponse>(`${EARLYSTAGE_ANALYTICS_PREFIX}/anomalies/stats/summary`, {
    params: { siteId: filters.site_id || filters.tenant_id },
  });

export const acknowledgeAnomaly = (traceId: string, notes?: string) =>
  analyticsApi.post(`/anomalies/${traceId}/acknowledge`, { notes });

export const resolveAnomaly = (
  traceId: string,
  resolution_notes: string,
  is_false_positive = false
) =>
  analyticsApi.post(`/anomalies/${traceId}/resolve`, { resolution_notes, is_false_positive });

// =====================
// Change Detection APIs
// =====================

export interface ChangeHistoryFilters {
  tenant_id?: number;
  page_url?: string;
  change_type?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}

export interface ChangeEvent {
  id?: string | number;
  page_id?: number;
  tenant_id?: number;
  change_type?: string;
  detected_at?: string;
  summary?: string;
}

export interface ChangeHistoryResponse {
  items: ChangeEvent[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export const fetchChangeHistory = (filters: ChangeHistoryFilters = {}) =>
  analyticsApi.get<ChangeHistoryResponse>('/change-detection/history', { params: filters });

export interface HtmlDiffRequest {
  before_html: string;
  after_html: string;
}

export interface HtmlDiffResponse {
  diff_html: string;
}

export const computeHtmlDiff = (payload: HtmlDiffRequest) =>
  analyticsApi.post<HtmlDiffResponse>('/change-detection/html-diff', payload);

// ============
// SERP APIs
// ============

export interface SerpAwarenessResponse {
  id: number;
  page_id: number;
  tenant_id: number;
  engine: string;
  computed_at: string;
  window_days: number;
  position_buckets: Record<string, number>;
  appearance_breakdown?: Record<string, number>;
  volatility_score: number;
  notes?: Record<string, string>;
  insights?: string[];
  total_impressions?: number;
  top3_percentage?: number;
  is_volatile?: boolean;
}

export interface SerpAwarenessHistoryResponse {
  snapshots: SerpAwarenessResponse[];
  start_date: string;
  end_date: string;
  total_count: number;
}

export const fetchSerpAwareness = (
  pageId: number,
  params: { tenant_id: number; window?: number; engine?: string }
) =>
  analyticsApi.get<SerpAwarenessResponse>(`/serp/${pageId}/serp-awareness`, {
    params: {
      tenant_id: params.tenant_id,
      window: params.window ?? 28,
      engine: params.engine ?? 'google',
    },
  });

export const fetchSerpAwarenessHistory = (
  pageId: number,
  params: {
    tenant_id: number;
    window?: number;
    engine?: string;
    start_date?: string;
    end_date?: string;
  }
) =>
  analyticsApi.get<SerpAwarenessHistoryResponse>(`/serp/${pageId}/serp-awareness/history`, {
    params: {
      tenant_id: params.tenant_id,
      window: params.window ?? 28,
      engine: params.engine ?? 'google',
      start_date: params.start_date,
      end_date: params.end_date,
    },
  });

// ==================
// Opportunity APIs
// ==================

export interface OpportunityFilters {
  site_id?: number;
  tenant_id?: number;
  min_impressions?: number;
  level?: 'HIGH' | 'MEDIUM' | 'LOW';
  limit?: number;
}

export interface OpportunityFactor {
  raw_value: number;
  normalized_value: number;
  weight: number;
  contribution: number;
}

export interface OpportunityFactors {
  oci_inverse: OpportunityFactor;
  traffic_potential: OpportunityFactor;
  position_gap: OpportunityFactor;
  ctr_gap: OpportunityFactor;
}

export interface Opportunity {
  id?: number;
  tenant_id: number;
  page_id: number;
  page_url: string;
  opportunity_score: number; // 0-100
  opportunity_level: 'HIGH' | 'MEDIUM' | 'LOW';
  oci_score: number;
  avg_impressions: number;
  avg_position: number;
  avg_ctr: number;
  computed_at: string;
  factors: OpportunityFactors;
}

export interface OpportunityListResponse {
  opportunities: Opportunity[];
  total_count: number;
  filters_applied: {
    limit: number;
    min_impressions: number;
    level?: string;
  };
}

export const fetchOpportunities = (filters: OpportunityFilters = {}) => {
  const tenantId = String(filters.tenant_id || filters.site_id || 1);
  return analyticsApi.get<OpportunityListResponse>(`${EARLYSTAGE_ANALYTICS_PREFIX}/opportunity/tenants/${tenantId}/opportunities/ranked`, {
    params: {
      limit: filters.limit || 50,
      minImpressions: filters.min_impressions || 10.0,
      level: filters.level,
    },
  });
};

export const fetchOpportunityDetail = (pageId: number, siteIdOrTenantId: number = 1) =>
  analyticsApi.get<Opportunity>(`${EARLYSTAGE_ANALYTICS_PREFIX}/opportunity/pages/${pageId}/opportunity`, {
    params: { tenant_id: siteIdOrTenantId },
  });

// ===========
// GA4 APIs
// ===========

export interface GA4MetricsFilters {
  page_id?: number;
  tenant_id?: number;
  days_back?: number;
}

export interface GA4Metrics {
  page_id?: number;
  bounce_rate?: number;
  time_on_page?: number;
  scroll_depth?: number;
  conversion_rate?: number;
  total_events?: number;
}

export interface GA4Event {
  id?: string | number;
  event_name?: string;
  event_count?: number;
  user_count?: number;
  event_value?: number;
  timestamp?: string;
}

export interface GA4EventsResponse {
  total?: number;
  items?: GA4Event[];
}

export const fetchGA4Metrics = (filters: GA4MetricsFilters = {}) => {
  const tenantId = filters.tenant_id || 1;
  return analyticsApi.get<GA4Metrics>(`/ga4/tenants/${tenantId}/metrics`, { params: { page_id: filters.page_id, days_back: filters.days_back } });
};

export const fetchGA4Events = (filters: GA4MetricsFilters & { limit?: number } = {}) => {
  const tenantId = filters.tenant_id || 1;
  return analyticsApi.get<GA4EventsResponse>(`/ga4/tenants/${tenantId}/events`, { params: { page_id: filters.page_id, days_back: filters.days_back, limit: filters.limit } });
};

// ==================
// Recommendations API
// ==================

export interface RecommendationAction {
  id?: string | number;
  title?: string;
  description?: string;
  type?: string;
  recommendation_type?: string;
  priority?: 'high' | 'medium' | 'low';
  impact_score?: number;
  effort_score?: number;
  page_id?: number;
  page_url?: string;
  tenant_id?: number;
  status?: 'pending' | 'implemented' | 'dismissed' | 'ACTIVE' | 'IMPLEMENTED' | 'DISMISSED';
  created_date?: string;
  due_date?: string;
  generated_at?: string;
  expires_at?: string;
  // Rich fields from API
  confidence_score?: number;
  impact_estimate?: 'HIGH' | 'MEDIUM' | 'LOW';
  priority_score?: number;
  evidence?: {
    gap?: number;
    oci_score?: number;
    opportunity_score?: number;
  };
  action_items?: string[];
  related_metrics?: {
    oci?: {
      oci_score?: number;
      oci_level?: string;
      factors?: {
        ctr_trend?: number;
        click_velocity?: number;
        momentum_state?: 'IMPROVING' | 'DECLINING' | 'NEUTRAL';
        position_change?: number;
        impression_growth?: number;
      };
    };
    opportunity?: {
      opportunity_score?: number;
      opportunity_level?: string;
      avg_ctr?: number;
      avg_position?: number;
      avg_impressions?: number;
    };
  };
}

export interface RecommendationsFilters {
  page_id?: number;
  site_id?: number;
  tenant_id?: number;
  status?: 'ACTIVE' | 'IMPLEMENTED' | 'DISMISSED' | 'EXPIRED';
  priority?: 'high' | 'medium' | 'low';
  limit?: number;
}

export interface RecommendationsResponse {
  total?: number;
  recommendations?: RecommendationAction[];
  items?: RecommendationAction[]; // Legacy support
}

export interface DashboardAnalyticsSummary {
  total_anomalies: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  active_count: number;
  opportunity_count: number;
  average_score?: number;
  health_bucket?: 'healthy' | 'needs_attention' | 'critical' | string;
  customer_name?: string;
  agency_id?: string;
  traffic?: number;
  clicks?: number;
  impressions?: number;
  customer_count?: number;
  website_count?: number;
  critical_site_count?: number;
  critical_issue_count?: number;
  open_issue_count?: number;
  healthy_site_count?: number;
  needs_attention_site_count?: number;
  needs_attention_count?: number;
  new_count?: number;
  resolved_count?: number;
  reports_due_count?: number;
}

export interface DashboardAnalyticsAnomaly {
  id: number;
  site_id: number;
  tenant_id?: number | string;
  metric: string;
  severity: AnomalySeverity;
  status: AnomalyStatus | string;
  category?: string;
  title?: string;
  description?: string;
  page?: string;
  created_at?: string;
  updated_at?: string;
}

export interface DashboardAnalyticsOpportunity {
  id?: number;
  site_id?: number;
  page_url?: string;
  opportunity_score?: number;
  opportunity_level?: string;
  page?: string;
}

export interface DashboardAnalyticsSite {
  id: number;
  name?: string;
  url?: string;
  tenant_id?: number | string;
  site_name?: string;
  site_url?: string;
  customer_id?: number | string;
  agency_id?: number | string;
  score?: number;
  health_bucket?: 'healthy' | 'needs_attention' | 'critical' | string;
  critical_issues?: number;
  high_issues?: number;
  medium_issues?: number;
  low_issues?: number;
  open_issues?: number;
  traffic?: number;
  clicks?: number;
  impressions?: number;
  trend?: 'improving' | 'stable' | 'declining' | string;
  score_delta?: number;
  last_refreshed_at?: string;
}

export interface DashboardHealthDistribution {
  healthy?: number;
  good?: number;
  issues?: number;
  critical?: number;
}

export interface DashboardIssueRollup {
  title?: string;
  severity?: string;
  status?: string;
  metric?: string;
}

export interface DashboardRecentActivity {
  title?: string;
  subtitle?: string;
  timestamp?: string;
}

export interface DashboardTrendPoint {
  name?: string;
  date?: string;
  score?: number;
  traffic?: number;
  searchPerformance?: number;
  ranking?: number;
  clicks?: number;
  impressions?: number;
  ctr?: number;
  avg_position?: number;
}

export interface DashboardReportItem {
  title?: string;
  period?: string;
  status?: string;
  generated_at?: string;
  delivered_at?: string;
  recipient?: string;
  action_label?: string;
}

export interface DashboardKeywordRanking {
  keyword?: string;
  position?: number;
  search_volume?: number;
}

export interface DashboardCustomerPortfolioRow {
  customer_id?: string;
  tenant_id?: string;
  customer?: string;
  avgSeoScore?: number;
  openIssues?: number;
  organicTraffic?: number | string;
  impressions?: number;
  website_count?: number;
  health_bucket?: 'healthy' | 'needs_attention' | 'critical' | string;
}

export interface DashboardAnalyticsResponse {
  scope: 'agency' | 'customer' | 'site';
  scope_id: string;
  summary: DashboardAnalyticsSummary;
  anomalies: DashboardAnalyticsAnomaly[];
  opportunities: DashboardAnalyticsOpportunity[];
  sites: DashboardAnalyticsSite[];
  health_distribution?: DashboardHealthDistribution;
  top_issues?: DashboardIssueRollup[];
  recent_activity?: DashboardRecentActivity[];
  seo_score_trend?: DashboardTrendPoint[];
  reports?: DashboardReportItem[];
  keyword_rankings?: DashboardKeywordRanking[];
  customer_portfolio_rows?: DashboardCustomerPortfolioRow[];
  search_performance?: DashboardTrendPoint[];
}

export const resolveDashboardScope = (isCustomerScopedUser: boolean, fallbackScopeId?: number | string | null) => {
  const scope = isCustomerScopedUser ? 'customer' : 'agency';
  const scopeId = fallbackScopeId ?? '';

  return {
    scope,
    scopeId,
  } as const;
};

export const fetchDashboardAnalytics = (scope: 'agency' | 'customer', scopeId: number | string, limit?: number) => {
  const normalizedScopeId = String(scopeId);
  const path = `${EARLYSTAGE_ANALYTICS_PREFIX}/dashboard/${scope}/${normalizedScopeId}`;
  return analyticsApi.get<DashboardAnalyticsResponse>(path, {
    params: limit === undefined ? undefined : { limit },
  });
};

export const fetchRecommendations = async (filters: RecommendationsFilters = {}) => {
  const tenantId = String(filters.site_id || filters.tenant_id || 1);
  const response = await analyticsApi.get<RecommendationsResponse>(`${EARLYSTAGE_ANALYTICS_PREFIX}/recommendations/tenants/${tenantId}`, {
    params: {
      page_id: filters.page_id,
      status: filters.status,
      priority: filters.priority,
      limit: filters.limit,
    },
  });

  // Map backend status to frontend status
  const mapStatus = (status?: string): 'pending' | 'implemented' | 'dismissed' => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'active' || statusLower === 'pending') return 'pending';
    if (statusLower === 'implemented' || statusLower === 'completed') return 'implemented';
    if (statusLower === 'dismissed' || statusLower === 'dismissed' || statusLower === 'skipped') return 'dismissed';
    return 'pending';
  };
  
  // Transform API response to match frontend interface
  const recommendations = (response.data.recommendations || response.data.items || []).map((rec) => ({
    id: rec.id,
    title: rec.title,
    description: rec.description,
    type: rec.recommendation_type?.toLowerCase().replace('_', '-') || rec.type,
    recommendation_type: rec.recommendation_type,
    priority: rec.impact_estimate?.toLowerCase() || rec.priority || 'medium',
    impact_score: rec.priority_score || rec.impact_score,
    effort_score: rec.effort_score,
    page_id: rec.page_id,
    page_url: rec.page_url,
    tenant_id: rec.tenant_id,
    status: mapStatus(rec.status),
    created_date: rec.generated_at || rec.created_date,
    due_date: rec.expires_at || rec.due_date,
    // Rich fields
    confidence_score: rec.confidence_score,
    impact_estimate: rec.impact_estimate,
    priority_score: rec.priority_score,
    evidence: rec.evidence,
    action_items: rec.action_items,
    related_metrics: rec.related_metrics,
  }));
  
  return {
    ...response,
    data: {
      recommendations,
      items: recommendations, // Legacy support
      total: response.data.total
    }
  };
};

export const dismissRecommendation = (recommendationId: string | number, tenantId: number = 1) =>
  analyticsApi.post(`${EARLYSTAGE_ANALYTICS_PREFIX}/recommendations/tenants/${tenantId}/${recommendationId}/dismiss`, {});

export const implementRecommendation = (recommendationId: string | number, tenantId: number = 1) =>
  analyticsApi.post(`${EARLYSTAGE_ANALYTICS_PREFIX}/recommendations/tenants/${tenantId}/${recommendationId}/implement`, {});
